import React from 'react';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Typography, useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { MemberImage } from './MemberImage';
import { MemberNotes } from './MemberNotes';
import { MemberHealth, MemberHealthControl } from './MemberHealthControl';
import { MemberEffectItem, MemberEffectsBase } from './MemberEffectsBase';
import { MemberTraitItem, MemberTraits } from './MemberTraits';
import { DisplayPermanentInjury, PermanentInjuries } from './PermanentInjuries';
import { createRivetedPanelSurface, hashStringToSeed } from '../../theme/surfaces';

export interface MemberActionMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  checked?: boolean;
}

interface MemberCardProps {
  warbandId: string;
  memberId: string;
  name: string;
  subtitle?: string;
  accessToken: string | null;
  editMode?: boolean;
  onEdit?: () => void;
  onActions?: (event: React.MouseEvent<HTMLElement>) => void;
  actionMenuItems?: MemberActionMenuItem[];
  statusContent?: React.ReactNode;
  statsContent?: React.ReactNode;
  health?: MemberHealth;
  healthContent?: React.ReactNode;
  injuries?: DisplayPermanentInjury[];
  onAddInjury?: () => void;
  onRemoveInjury?: (injury: DisplayPermanentInjury, index: number) => void;
  injuriesContent?: React.ReactNode;
  lowerContent?: React.ReactNode;
  traits?: MemberTraitItem[];
  effects?: MemberEffectItem[];
  onAddEffect?: () => void;
  addEffectDisabled?: boolean;
  footerContent?: React.ReactNode;
  notes?: string | null;
  onSaveNotes?: (notes: string) => void;
  opacity?: number;
  cardSx?: object;
  overlay?: React.ReactNode;
  children?: React.ReactNode;
}

export const MemberCard: React.FC<MemberCardProps> = ({
  warbandId,
  memberId,
  name,
  subtitle,
  accessToken,
  editMode = false,
  onEdit,
  onActions,
  actionMenuItems,
  statusContent,
  statsContent,
  health,
  healthContent,
  injuries,
  onAddInjury,
  onRemoveInjury,
  injuriesContent,
  lowerContent,
  traits,
  effects,
  onAddEffect,
  addEffectDisabled,
  footerContent,
  notes,
  onSaveNotes,
  opacity = 1,
  cardSx,
  overlay,
  children,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const hasMenu = (actionMenuItems?.length ?? 0) > 0;
  // Stargrave (dark theme): vary the riveted texture per member.
  const isDark = useTheme().palette.mode === 'dark';
  const memberSurfaceSx = React.useMemo(
    () => (isDark ? createRivetedPanelSurface(hashStringToSeed(memberId)) : {}),
    [isDark, memberId]
  );
  return (
  <Paper variant="outlined" sx={{ p: 2, opacity, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', ...memberSurfaceSx, ...cardSx }}>
    {overlay}
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start" gap={{ xs: 1.5, sm: 2 }}>
      <MemberImage warbandId={warbandId} memberId={memberId} editMode={editMode} accessToken={accessToken} />
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <Typography variant="h6">{name}</Typography>
              {editMode && onEdit && <IconButton size="small" aria-label={`Edit ${name}`} onClick={onEdit}><EditIcon fontSize="small" /></IconButton>}
            </Stack>
            {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
          </Box>
          <Stack direction="row" alignItems="center" gap={0.5}>
            {statusContent}
            {(onActions || hasMenu) && <IconButton size="small" aria-label={`${name} actions`} onClick={onActions ?? ((event) => setMenuAnchorEl(event.currentTarget))}><MoreVertIcon /></IconButton>}
          </Stack>
        </Stack>
        {statsContent}
        {health && <MemberHealthControl {...health} />}
        {healthContent}
        {injuries && <PermanentInjuries injuries={injuries} editMode={editMode} onAdd={onAddInjury} onRemove={onRemoveInjury} />}
        {injuriesContent}
      </Box>
    </Stack>
    {lowerContent}
    {traits && <MemberTraits traits={traits} />}
    {(effects || onAddEffect) && <MemberEffectsBase effects={effects ?? []} onAdd={onAddEffect} addDisabled={addEffectDisabled} />}
    {(onSaveNotes || notes != null) && <MemberNotes notes={notes} editMode={editMode} onSaveNotes={onSaveNotes} />}
    {/* mt: 'auto' pins the footer to the card bottom when grid rows stretch. */}
    {footerContent && <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 'auto', pt: 1 }}>{footerContent}</Box>}
    {hasMenu && <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
      {actionMenuItems!.map((item) => <MenuItem key={item.label} disabled={item.disabled} onClick={() => { setMenuAnchorEl(null); item.onClick(); }}>
        {item.checked !== undefined && <ListItemIcon>{item.checked ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>}
        <ListItemText inset={item.checked === undefined ? undefined : false}>{item.label}</ListItemText>
      </MenuItem>)}
    </Menu>}
    {children}
  </Paper>
  );
};
